/* Small, progressively enhanced interactions. Native video and details work without JS. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const animate = (node, distance = 24, duration = 760, delay = 0) => {
    if (!node || reduceMotion.matches || !node.animate) return;
    node.animate([{ opacity: 0.5, transform: `translateY(${distance}px)` }, { opacity: 1, transform: 'translateY(0)' }], { duration, delay, easing: 'cubic-bezier(.22,1,.36,1)' });
  };
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      const node = entry.target;
      const card = node.classList.contains('el-card');
      const delay = card ? [...node.parentElement.children].indexOf(node) % 4 * 85 : 0;
      animate(node, 28, 820, delay);
      const photo = node.matches('.el-featured__media, .el-story__media, .el-color-story__image, .el-rhythm__panels') ? node.querySelector('img') : null;
      if (photo && !reduceMotion.matches) photo.animate([{ transform:'scale(1.045)' },{ transform:'scale(1)' }],{duration:1100,easing:'cubic-bezier(.22,1,.36,1)'});
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -25px 0px' }) : null;
  function init(root = document) {
    root.querySelectorAll('[data-el-rhythm]').forEach(group => {
      if (group.dataset.elReady) return;
      group.dataset.elReady = 'true';
      const tabs = [...group.querySelectorAll('[data-el-tab]')];
      const panels = [...group.querySelectorAll('[data-el-panel]')];
      function activate(tab, focus = false) {
        tabs.forEach(item => { const active = item === tab; item.setAttribute('aria-selected', String(active)); item.tabIndex = active ? 0 : -1; });
        panels.forEach(panel => { panel.hidden = panel.id !== tab.getAttribute('aria-controls'); if (!panel.hidden) animate(panel, 3, 240); });
        if (focus) tab.focus();
      }
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', e => {
          let next;
          if (e.key === 'ArrowRight') next = (index + 1) % tabs.length;
          if (e.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
          if (e.key === 'Home') next = 0;
          if (e.key === 'End') next = tabs.length - 1;
          if (next !== undefined) { e.preventDefault(); activate(tabs[next], true); }
        });
      });
    });
    root.querySelectorAll('[data-el-color-view]').forEach(group => {
      if (group.dataset.elColorReady) return;
      group.dataset.elColorReady = 'true';
      const buttons = [...group.querySelectorAll('[data-el-color]')];
      const photo = group.querySelector('.el-color-photo');
      const overlay = group.querySelector('[data-el-color-overlay]');
      const label = group.querySelector('[data-el-color-label]');
      const originalLabel = label?.textContent;
      let colorRequest = 0;
      const reset = () => {
        colorRequest += 1;
        if (overlay) overlay.hidden = true;
        buttons.forEach(button => button.setAttribute('aria-pressed', 'false'));
        if (label) label.textContent = originalLabel;
      };
      buttons.forEach(button => button.addEventListener('click', () => {
        if (!photo) return;
        const request = ++colorRequest;
        const next = new Image();
        next.onload = () => {
          if (request !== colorRequest) return;
          photo.removeAttribute('srcset');
          photo.src = next.src;
          photo.alt = button.dataset.alt;
          buttons.forEach(item => item.setAttribute('aria-pressed', String(item === button)));
          if (overlay) overlay.hidden = false;
          if (label) label.textContent = button.dataset.label;
          group.style.setProperty('--el-current-glow', button.style.getPropertyValue('--swatch'));
          animate(photo, 0, 380);
        };
        next.src = button.dataset.image;
      }));
      group.querySelector('[data-el-color-reset]')?.addEventListener('click', reset);
      group.querySelectorAll('[data-hp-thumb]').forEach(thumb => thumb.addEventListener('click', reset));
      if (!overlay && buttons[0]) {
        buttons[0].setAttribute('aria-pressed', 'true');
        if (label) label.textContent = buttons[0].dataset.label;
      }
    });
    if (observer && !reduceMotion.matches && !window.Shopify?.designMode) {
      root.querySelectorAll('.el-story__media, .el-story__copy, .el-rhythm__intro, .el-rhythm__panels, .el-featured__media, .el-featured__copy, .el-video-copy, .el-video-frame, .el-color-story__image, .el-color-story__copy, .el-section-head, .el-card, .el-faq__intro, .el-faq__list').forEach(node => {
        if (node.dataset.elRevealReady) return;
        node.dataset.elRevealReady = 'true';
        if (node.getBoundingClientRect().top >= window.innerHeight) observer.observe(node);
      });
    }
    root.querySelectorAll('details').forEach(detail => {
      if (detail.dataset.elMotionReady) return;
      detail.dataset.elMotionReady = 'true';
      detail.addEventListener('toggle', () => {
        if (!detail.open) return;
        [...detail.children].filter(node => node.tagName !== 'SUMMARY').forEach(node => animate(node, -5, 280));
      });
    });
    root.querySelectorAll('[data-el-video]').forEach(video => {
      if (video.dataset.elReady) return;
      video.dataset.elReady = 'true';
      const player = video.closest('[data-el-player]');
      const toggle = player?.querySelector('[data-el-film-toggle]');
      const sound = player?.querySelector('[data-el-film-sound]');
      const progress = player?.querySelector('[data-el-film-progress]');
      const tools = player?.querySelector('[data-el-film-tools]');
      let manualPause = false;
      let observerPause = false;
      let visible = false;
      let viewObserver;
      const update = () => {
        player?.classList.toggle('is-playing', !video.paused);
        if (toggle) toggle.textContent = video.paused ? 'Afspil film' : 'Sæt på pause';
        if (sound) { sound.textContent = video.muted ? 'Slå lyd til' : 'Slå lyd fra'; sound.setAttribute('aria-pressed', String(!video.muted)); }
      };
      const play = () => { if (document.hidden) return; video.play().catch(() => update()); };
      const pause = () => { if (!video.paused) { observerPause = true; video.pause(); } };
      if (tools) tools.hidden = false;
      video.muted = true;
      toggle?.addEventListener('click', () => {
        if (video.paused) { manualPause = false; play(); }
        else { manualPause = true; video.pause(); }
      });
      sound?.addEventListener('click', () => { video.muted = !video.muted; update(); });
      video.addEventListener('play', () => {
        manualPause = false;
        document.querySelectorAll('[data-el-video]').forEach(other => { if (other !== video) other.pause(); });
        update();
      });
      video.addEventListener('pause', () => {
        if (observerPause) observerPause = false;
        else if (visible) manualPause = true;
        update();
      });
      video.addEventListener('volumechange', update);
      video.addEventListener('timeupdate', () => {
        if (progress && Number.isFinite(video.duration) && video.duration > 0) progress.style.transform = `scaleX(${video.currentTime / video.duration})`;
      });
      const canAutoPlay = () => video.hasAttribute('data-el-play-on-view') && !reduceMotion.matches && !navigator.connection?.saveData && !window.Shopify?.designMode;
      if ('IntersectionObserver' in window) {
        viewObserver = new IntersectionObserver(entries => {
          visible = entries[0].intersectionRatio >= 0.38;
          if (visible && canAutoPlay() && !manualPause) play();
          else if (!visible) pause();
        }, { threshold: [0,0.38] });
        viewObserver.observe(video);
      }
      const visibility = () => { if (document.hidden) pause(); else if (visible && canAutoPlay() && !manualPause) play(); };
      const preference = () => { if (reduceMotion.matches) pause(); };
      document.addEventListener('visibilitychange', visibility);
      reduceMotion.addEventListener('change', preference);
      const section = video.closest('.shopify-section');
      section?.addEventListener('shopify:section:unload', () => { viewObserver?.disconnect(); document.removeEventListener('visibilitychange', visibility); reduceMotion.removeEventListener('change', preference); }, {once:true});
      update();
    });

  }
  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', e => init(e.target));
  document.addEventListener('shopify:block:select', e => {
    const panel = e.target.closest('[data-el-panel]');
    if (!panel) return;
    document.getElementById(panel.getAttribute('aria-labelledby'))?.click();
  });
})();
